import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../tps.css';
import { useLocation } from 'react-router-dom'; // ✅ Added

const AuthorTotalPaper = () => {
  const [papers, setPapers] = useState([]);
  const [filteredPapers, setFilteredPapers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const location = useLocation(); // ✅ Added

  const token = localStorage.getItem('authToken');
  const platformId = localStorage.getItem('platformID');
  const API = process.env.REACT_APP_API_BASE_URL;
  let authorID = localStorage.getItem('AuthorID');

  if (token) {
    const decoded = jwtDecode(token);
    authorID = decoded.id;
  }

  // ✅ Apply filters passed via React Router state (from dashboard)
  useEffect(() => {
    if (location.state) {
      const { defaultStatusFilter, defaultTypeFilter } = location.state;
      if (defaultStatusFilter) setStatusFilter(defaultStatusFilter);
      if (defaultTypeFilter) setTypeFilter(defaultTypeFilter);
    }
  }, [location.state]);

  useEffect(() => {
    if (!platformId || !authorID) return;

    axios
      .get(`${API}/api/author/papers/${platformId}/${authorID}`)
      .then((res) => {
        setPapers(res.data.totalPaper || []);
        // console.log(res.data)
        setPage(1);
      })
      .catch((err) => {
        console.error('Error fetching papers:', err);
      });
  }, [platformId, authorID]);

  // Apply filters and remap pending+reviewed to Accept
  useEffect(() => {
    let updated = papers;

    // Remap logic
    updated = updated.map((paper) =>
      paper.Status?.toLowerCase() === 'pending' && paper.IsReview === 'Reviewed'
        ? { ...paper, Status: 'Accept' }
        : paper
    );

    // Filter by status
    if (statusFilter !== 'all') {
      updated = updated.filter(
        (p) => p.Status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Filter by paper type
    if (typeFilter !== 'all') {
      const type = typeFilter.toLowerCase();
      updated = updated.filter((p) =>
        p.SubmissionTitle?.toLowerCase().includes(type)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      updated = updated.filter((p) =>
        p.SubmissionTitle?.toLowerCase().includes(term)
      );
    }

    setFilteredPapers(updated);
    setPage(1);
  }, [papers, searchTerm, statusFilter, typeFilter]);

  const paginatedPapers = filteredPapers.slice((page - 1) * perPage, page * perPage);

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Author Submission History', 14, 22);

    autoTable(doc, {
      startY: 30,
      head: [['Title', 'Type', 'Status', 'Date']],
      body: filteredPapers.map((p) => [
        p.SubmissionTitle,
        getType(p.SubmissionTitle),
        p.Status,
        new Date(p.submissiondate).toLocaleDateString()
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      theme: 'grid'
    });

    doc.save('author_submissions.pdf');
  };

  // Export to Excel
  const exportToExcel = () => {
    const data = filteredPapers.map((p) => ({
      Title: p.SubmissionTitle,
      Type: getType(p.SubmissionTitle),
      Status: p.Status,
      Date: new Date(p.submissiondate).toLocaleDateString()
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');
    XLSX.writeFile(workbook, 'author_submissions.xlsx');
  };

  // Get paper type from title
  const getType = (title = '') => {
    const lower = title.toLowerCase();
    if (lower.includes('abstract')) return 'Abstract';
    if (lower.includes('full')) return 'Full';
    if (lower.includes('revision')) return 'Revision';
    return 'Unknown';
  };

  return (
    <div className="container mt-5 ml-2">
      <h3 className="page-title mb-4">Author Submission History</h3>

      {/* Search + Filters */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control mb-3"
          style={{ maxWidth: '300px' }}
          placeholder="Search by Title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="d-flex flex-wrap gap-4">
          {/* Status Filter */}
          <div>
            <strong>Status:</strong>
            {['all', 'pending', 'accept', 'reject'].map((status) => (
              <div className="form-check" key={status}>
                <input
                  type="radio"
                  className="form-check-input"
                  id={`status-${status}`}
                  value={status}
                  checked={statusFilter === status}
                  onChange={() => setStatusFilter(status)}
                />
                <label className="form-check-label" htmlFor={`status-${status}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </label>
              </div>
            ))}
          </div>

          {/* Paper Type Filter */}
          <div>
            <strong>Paper Type:</strong>
            {['all', 'abstract', 'full', 'revision'].map((type) => (
              <div className="form-check" key={type}>
                <input
                  type="radio"
                  className="form-check-input"
                  id={`type-${type}`}
                  value={type}
                  checked={typeFilter === type}
                  onChange={() => setTypeFilter(type)}
                />
                <label className="form-check-label" htmlFor={`type-${type}`}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle text-center">
          <thead className="table-primary">
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Submitted On</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPapers.length > 0 ? (
              paginatedPapers.map((paper, idx) => (
                <tr key={idx}>
                  <td>{paper.SubmissionTitle}</td>
                  <td>{getType(paper.SubmissionTitle)}</td>
                  <td>
                    <span className={`badge ${
                      (paper.Status?.toLowerCase() === 'accept') ? 'bg-success' :
                      (paper.Status?.toLowerCase() === 'pending') ? 'bg-warning text-dark' :
                      (paper.Status?.toLowerCase() === 'reviewed') ? 'bg-info text-dark' :
                      (paper.Status?.toLowerCase() === 'reject') ? 'bg-danger' :
                      'bg-secondary'
                    }`}>
                      {paper.Status || 'Unknown'}
                    </span>
                  </td>
                  <td>{new Date(paper.submissiondate).toLocaleDateString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">No matching papers found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          className="btn btn-secondary"
          onClick={() => setPage((p) => p + 1)}
          disabled={page * perPage >= filteredPapers.length}
        >
          Next
        </button>
      </div>

      {/* Export Buttons */}
      {filteredPapers.length > 0 && (
        <div className="mt-3">
          <button className="btn btn-success me-2" onClick={exportToPDF}>
            Export to PDF
          </button>
          <button className="btn btn-outline-primary" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthorTotalPaper;
